import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useCardProvider } from "Payments/globalPaymentsStateHooks";
import classNames from "classnames";
import { motion } from "framer-motion";

import CardBackgrounds from "./CardBackgrounds";
import CardNumberInput from "./CardNumberInput";
import Cvc from "./Cvc";
import ExpirationDate from "./ExpirationDate";
import NameInput from "./NameInput";
import CreditCardVideoOCR from "./OCR/CreditCardVideoOCR";
import Zip from "./Zip";
import useCreditCardEntryState from "./state";
import { Shine, iPhone, iPhoneMini } from "./styles";
import useStaxAPI from "./useStaxAPI";

export default function CreditCard({
  defaultName,
  defaultZip,
  isAlreadyExisting,
  show,
  setInputErrors,
  shouldClearDisplayMessages,
  setShouldClearDisplayMessages,
  submitting,
  ...props
}) {
  const { providerIsStax } = useCardProvider();
  const {
    initializeStaxForm,
    staxIsLoaded: alreadyLoaded,
    loadStaxJs,
  } = useStaxAPI();

  let staxIsLoaded = alreadyLoaded;
  if (!staxIsLoaded) {
    staxIsLoaded = loadStaxJs();
  }

  const cardInputRef = useRef();
  const expirationInputRef = useRef();
  const cvcInputRef = useRef();
  const nameInputRef = useRef();
  const zipInputRef = useRef();

  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  const [
    {
      cardType,
      ocr: { show: showOCR, hasStream },
      displayErrorMessages,
      isEditing,
      canBeEdited,
    },
    dispatch,
  ] = useCreditCardEntryState();

  const cardTypeName = cardType?.name || "";

  useEffect(() => {
    const errors = shouldClearDisplayMessages ? [] : displayErrorMessages;
    setInputErrors(errors);
    if (shouldClearDisplayMessages) {
      dispatch({ type: "Clear Display Errors" });
      setShouldClearDisplayMessages(false);
    }
  }, [
    displayErrorMessages,
    setInputErrors,
    shouldClearDisplayMessages,
    setShouldClearDisplayMessages,
    dispatch,
  ]);

  const cardTypeClass = classNames({ has_card_type: cardTypeName });

  useEffect(
    function initStaxForm() {
      if (!staxIsLoaded) return;

      if (
        providerIsStax &&
        ((isEditing && canBeEdited) || !isAlreadyExisting)
      ) {
        initializeStaxForm();
      }
    },
    [
      staxIsLoaded,
      providerIsStax,
      isEditing,
      canBeEdited,
      isAlreadyExisting,
      initializeStaxForm,
    ]
  );

  return (
    <Wrapper>
      <Card
        initial={show ? {} : { scale: 0.8, y: "20%", opacity: 0 }}
        animate={
          show
            ? {
                scale: 1,
                y: 0,
                opacity: 1,
                rotateY: showOCR && hasStream ? 180 : 0,
                transition: hasAnimatedIn ? { duration: 0.4 } : { delay: 0.5 },
              }
            : {}
        }
        className={cardTypeClass}
        onAnimationComplete={() => setHasAnimatedIn(true)}
        //{...props}
      >
        <Shine />
        <CardBackgrounds
          cardType={cardTypeName}
          isAlreadyExisting={isAlreadyExisting && !isEditing}
        />
        <Border />
        <Inside>
          <CardNumberInput
            ref={cardInputRef}
            expirationInputRef={expirationInputRef}
            submitting={submitting}
            paymentMethodLast4={props?.last4}
          />
          <Line2>
            <ExpirationDate
              ref={expirationInputRef}
              cardInputRef={cardInputRef}
              cvcInputRef={cvcInputRef}
              submitting={submitting}
            />
            <Spacer />
            <Cvc
              ref={cvcInputRef}
              expirationInputRef={expirationInputRef}
              nameInputRef={nameInputRef}
              zipInputRef={zipInputRef}
              submitting={submitting}
            />
          </Line2>
          <Line3>
            <NameInput
              ref={nameInputRef}
              defaultName={defaultName}
              submitting={submitting}
            />
            <Zip
              ref={zipInputRef}
              defaultZip={defaultZip}
              submitting={submitting}
            />
          </Line3>
        </Inside>
      </Card>
      <CreditCardVideoOCR />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  max-width: min(400px, calc(100vw - 16px));
  position: relative;
  /*
  NOTE: OCR uses this to flip the card around, but leaving it causes flickering on the payments page input amount and other elements when focusing/blurring/hovering around Outline (styles.js).  Removing Outline's transition property also fixes it.
  perspective: 1000px;
  */
  box-sizing: border-box;
`;

const Card = styled(motion.div)`
  width: 100%;
  aspect-ratio: 85.6 / 53.98;

  @supports not (aspect-ratio: auto) {
    height: min(252px, calc((100vw - 16px) * 53.98 / 85.6));
  }

  border-radius: 16px;
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.3) 0 2px 8px;
  overflow: hidden;
  z-index: 100;
  /*
  NOTE: OCR uses this to flip the card around, but leaving it causes flickering on the payments page input amount and other elements when focusing/blurring/hovering around Outline (styles.js).  Removing Outline's transition property also fixes it.
  backface-visibility: hidden;
  */

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    transition: none;
    transition-delay: 0s;
    background: linear-gradient(
      to right top,
      #280537,
      #56034c,
      #890058,
      #bc005b,
      #eb1254
    );
  }
  &.has_card_type::before {
    transition: opacity 0.4s;
    transition-delay: 0.4s;
    opacity: 0;
  }
`;

const Border = styled.div`
  position: absolute;
  top: 8px;
  bottom: 8px;
  right: 8px;
  left: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  z-index: 1;
`;

const Inside = styled.div`
  position: absolute;
  top: 32px;
  left: 32px;
  right: 32px;
  bottom: 12px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: rgba(255, 255, 255, 0.9);
  @media screen and (max-width: ${iPhone.cssWidth}) {
    top: 18px;
    left: 24px;
    right: 24px;
    bottom: 18px;
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    top: 16px;
    left: 16px;
    right: 16px;
    bottom: 16px;
  }
`;

const Line2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 9px;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    margin-top: 12px;
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    margin-top: 10px;
  }
`;

const Spacer = styled.div`
  flex-grow: 1;
`;

const Line3 = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
